<i18n
  src="@/locales/views/packing_house/production/final_packing_list_simplified/show_container.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_process')"
    :params="{
      packing_house_process_id: this.packing_house_process_id,
    }"
  ></go-back-link>

  <bread-crumbs
    :labels="[module_name, packing_house_process?.name, container_label]"
  />

  <template v-if="load_general_data">
    <div class="mt-6">
      <container-info :container="container" />
    </div>

    <div class="flex flex-row justify-between mt-8">
      <div class="panel-plain-label">
        <badge-default
          :label="$t('labels.total_packages', { total: total_packages })"
        />
      </div>
      <div class="flex justify-end">
        <template
          v-for="(packing_list_format, idx) in packing_list_formats"
          :key="idx"
        >
          <button
            type="button"
            @click.prevent="downloadPackingListXlsx(packing_list_format)"
            class="btn-white"
          >
            <i
              :class="[
                'fa-solid',
                packing_list_format.downloading
                  ? 'fa-sync fa-spin'
                  : 'fa-file-excel',
              ]"
            ></i>
            <span class="space-icon"></span>{{ packing_list_format.name }}
          </button>
          <span class="space-actions" />
        </template>
        <button
          type="button"
          @click.prevent="goToNewPackages"
          class="btn-white"
        >
          <i class="fa-solid fa-plus"></i><span class="space-icon"> </span
          >{{ $t("labels.new_button") }}
        </button>
      </div>
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.producer") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.exported_batch.crop_field") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.exporter_process_crop_category") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.exporter_process_crop_variety") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.exporter_process_brand") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.exporter_process_crop_package") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.package.quantity") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="package_item in packages"
                  :key="package_item.id"
                >
                  <td class="table-cell">
                    {{ getProducerLabelByPackage(package_item) }}
                  </td>
                  <td class="table-cell">
                    {{ package_item.exported_batch.crop_field.name }}
                  </td>
                  <td class="table-cell">
                    {{
                      package_item.exporter_process_crop_category
                        .agro_exporter_crop_category.crop_category.name
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      package_item.exporter_process_crop_variety
                        .agro_exporter_crop_variety.crop_variety.name
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      package_item.exporter_process_brand.agro_exporter_brand
                        .name
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      $t("model.package.exporter_process_crop_package_value", {
                        name: package_item.exporter_process_crop_package.name,
                        weight:
                          package_item.exporter_process_crop_package
                            .gross_weight,
                      })
                    }}
                  </td>
                  <td scope="col" class="table-cell">
                    {{ package_item.quantity }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="py-6 text-right">
      <button type="button" @click.prevent="goToNewPackages" class="btn-white">
        <i class="fa-solid fa-plus pr-2"></i>
        {{ $t("labels.new_button") }}
      </button>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/packing_house/requesters/production/final_packing_list_simplified/show_container.js";
import ContainerInfo from "@/components/views/packing_house/production/final_packing_list_simplified/ContainerInfo.vue";
export default {
  name: "PHProductionFinalPackingListSimplifiedShowContainer",
  components: {
    ContainerInfo,
  },
  data() {
    return {
      load_general_data: false,
      packing_house_process: {},
      container: null,
      packing_list_formats: [],
      packages: [],
    };
  },
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: { required: true },
    container_id: { required: true },
  },
  computed: {
    container_code() {
      return this.container ? this.container.container_code : "";
    },
    container_label: function () {
      let label = this.container_code;
      const brand_name =
        this.container?.exporter_process_brand?.agro_exporter_brand?.name;
      if (brand_name) label = `${label} (${brand_name})`;
      return label;
    },
    total_packages: function () {
      return this.packages.reduce((total, pkg) => {
        return total + (Number(pkg.quantity) || 0);
      }, 0);
    },
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess(
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.packing_house_process_id,
        this.container_id
      );
      this.container = response.data;
    },
    fetchPackingListFormats: async function () {
      let response = await Requester.fetchPackingListFormats(
        this.packing_house_process_id,
        this.container_id
      );
      this.packing_list_formats = response.data;

      this.packing_list_formats.forEach((packing_list_format) => {
        packing_list_format["downloading"] = false;
      });
    },
    fetchPackages: async function () {
      let response = await Requester.fetchPackages(
        this.packing_house_process_id,
        this.container_id
      );
      this.packages = response.data;
    },
    loadData: async function () {
      await this.fetchPackingHouseProcess();
      await this.fetchContainer();
      this.fetchPackingListFormats();
      await this.fetchPackages();
      this.load_general_data = true;
    },
    getProducerLabelByPackage: function (package_item) {
      if (package_item.producer && package_item.organization)
        return `${package_item.producer.fullname} (${package_item.organization.name})`;
      else if (package_item.producer) return package_item.producer.fullname;
      else if (package_item.organization) return package_item.organization.name;

      return "";
    },
    downloadPackingListXlsx: async function (packing_list_format) {
      packing_list_format.downloading = true;
      try {
        let response = await Requester.downloadPackingListXlsx(
          this.packing_house_process_id,
          this.container_id,
          packing_list_format.id
        );
        const file = response.data;
        this.downloadFile(file, `PackingList-${this.container_code}.xlsx`);
      } catch (error) {
        error;
      } finally {
        packing_list_format.downloading = false;
      }
    },
    goToNewPackages: function () {
      this.$router.push({
        name: this.routeName("assign_packages"),
        params: {
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id,
        },
      });
    },
  },
  created: function () {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.loadData();
      })
    );
  },
};
</script>
